//
// ============================================================================
// IMPORTS
// ============================================================================
//

import { readonly } from "vue";

// Types
import type { PendingSample } from "../../../services/api/domains/pending/pending.types";
import type { useValidationSelection } from "./useValidationSelection";

//
// ============================================================================
// TYPES
// ============================================================================
//

/**
 * API result item from backend
 */
interface ApiResultItem {
  status: 1 | 2; // 1 = success, 2 = error
  error?: string;
  id?: number;
}

/**
 * API response can be array of results, boolean, or null
 */
type ApiResponse = ApiResultItem[] | boolean | null;

/**
 * Validated response structure
 */
interface ValidatedResponse {
  type: "null" | "success" | "partial" | "error" | "unknown";
  errors: ApiResultItem[];
  successes: ApiResultItem[];
  data: any;
}

/**
 * Snackbar type options
 */
type SnackbarType = "success" | "error" | "warning" | "info";

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Validation Actions Composable
 *
 * Manages result accept/reject operations with comprehensive error handling.
 *
 * Responsibilities:
 * - API calls for accepting/rejecting individual results
 * - Bulk accept/reject operations for entire samples
 * - API response validation and error parsing
 * - User notification via snackbar (success/error/warning)
 * - Loading state management per result and per sample
 * - Optimistic UI updates (remove from pending, add to current)
 * - Visit data refresh after acceptance (to sync with LIS)
 *
 * @param key - Unique key for state isolation (default: "default")
 */
export const useValidationActions = (key: string = "default") => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  // Localization
  const { t } = useI18n();

  // Snackbar
  const { showSnackbar } = useSnackbar();

  // Error handling
  const { handleError } = useErrorBoundary();

  //
  // ============================================================================
  // STATE
  // ============================================================================
  //

  // Set of result IDs currently being accepted (prevents double-clicks)
  const acceptingResults = useState<Set<number>>(`${key}.acceptingResults`, () => new Set());

  // Set of result IDs currently being rejected
  const rejectingResults = useState<Set<number>>(`${key}.rejectingResults`, () => new Set());

  // Flag indicating bulk accept operation in progress
  const acceptingAll = useState<boolean>(`${key}.acceptingAll`, () => false);

  // Flag indicating bulk reject operation in progress
  const rejectingAll = useState<boolean>(`${key}.rejectingAll`, () => false);

  //
  // ============================================================================
  // PRIVATE HELPERS
  // ============================================================================
  //

  /**
   * Show result action snackbar with consistent formatting
   *
   * Purpose:
   * - Display action feedback to user with appropriate styling
   * - Prepend test name for context (e.g., "Glucose – Accepted")
   * - Use consistent icons and colors for action types
   *
   * Responsibilities:
   * - Map snackbar type to Material Design icon
   * - Apply appropriate color (success/error/warning/info)
   * - Set timeout based on severity (errors show longer: 2.5s vs 1.5s)
   * - Format message with test name prefix if provided
   *
   * @param type - Snackbar type (success/error/warning/info)
   * @param message - Message to display
   * @param testName - Optional test name to prepend
   */
  const showResultSnackbar = (type: SnackbarType, message: string, testName?: string): void => {
    const icons: Record<SnackbarType, string> = {
      success: "mdi-check-circle",
      error: "mdi-alert-circle",
      warning: "mdi-alert-circle",
      info: "mdi-information",
    };

    const colors: Record<SnackbarType, string> = {
      success: "success",
      error: "error",
      warning: "warning",
      info: "info",
    };

    const finalMessage = testName ? `${testName} – ${message}` : message;

    showSnackbar(finalMessage, {
      icon: icons[type],
      color: colors[type],
      timeout: type === "error" ? 2500 : 1500,
    });
  };

  /**
   * Validate API response and return structured result
   *
   * Purpose:
   * - Normalize diverse API response formats into consistent structure
   * - Distinguish between complete success, partial failure, and total failure
   * - Extract error messages and success indicators
   *
   * Responsibilities:
   * - Handle null responses (API errors caught by usePending)
   * - Handle boolean responses (deleteResult returns true/false)
   * - Handle array responses (bulk operations return status array)
   * - Classify response type: null/success/partial/error/unknown
   * - Separate successful items from failed items
   *
   * @param response - Raw API response (null | boolean | ApiResultItem[])
   * @returns Structured validation result with type and error/success arrays
   */
  const validateResponse = (response: any): ValidatedResponse => {
    // Handle null response (error in usePending)
    if (response === null) {
      return { type: "null", errors: [], successes: [], data: null };
    }

    // Handle boolean response (deleteResult returns boolean)
    if (typeof response === "boolean") {
      return {
        type: response ? "success" : "error",
        errors: [],
        successes: [],
        data: response,
      };
    }

    // Handle non-array response
    if (!Array.isArray(response)) {
      return { type: "unknown", errors: [], successes: [], data: response };
    }

    // Parse array response
    const errors = response.filter((r) => r.status === 2 && r.error);
    const successes = response.filter((r) => r.status === 1);

    let type: ValidatedResponse["type"] = "success";
    if (errors.length > 0 && successes.length === 0) {
      type = "error";
    } else if (errors.length > 0 && successes.length > 0) {
      type = "partial";
    }

    return { type, errors, successes, data: response };
  };

  //
  // ============================================================================
  // PUBLIC API - SINGLE RESULT ACTIONS
  // ============================================================================
  //

  /**
   * Accept a single result and publish to LIS
   *
   * Purpose:
   * - Validate and accept a laboratory result for patient record
   * - Optimistically update UI while syncing with backend
   * - Handle partial acceptance when multiple results exist for same test
   *
   * Responsibilities:
   * - Track loading state to prevent double-submission
   * - Call publishResult API to accept in LIS system
   * - Validate API response and handle errors gracefully
   * - Determine if this is the last pending result for the test
   * - Refresh visit data ONLY if last result (to show in "Current" column)
   * - Invoke success callback with accepted value for UI update
   * - Show user notification (success or error with details)
   *
   * Flow:
   * 1. Add result ID to acceptingResults set (disable UI)
   * 2. Find result in pending list and check if it's the last for its test
   * 3. Call API to publish result to LIS
   * 4. Validate response (null/error/success/partial/unknown)
   * 5. If null or error: Show error message and abort
   * 6. If success: Show success message
   * 7. If last result for test: Refresh visit to get updated current value
   * 8. Invoke onSuccess callback with result ID and accepted value
   * 9. Remove from acceptingResults set (re-enable UI)
   *
   * @param resultId - ID of the result to accept
   * @param testName - Display name of the test (e.g., "Холестерол - общ")
   * @param pendingComposable - Pending data composable instance
   * @param lisComposable - LIS data composable instance
   * @param selectionComposable - Validation selection composable instance
   * @param onSuccess - Callback invoked on successful acceptance (receives resultId, acceptedValue)
   */
  const acceptResult = async (
    resultId: number,
    testName: string,
    pendingComposable: ReturnType<typeof usePending>,
    lisComposable: ReturnType<typeof useLis>,
    selectionComposable: ReturnType<typeof useValidationSelection>,
    onSuccess: (resultId: number, acceptedValue: string) => Promise<void>,
  ): Promise<void> => {
    acceptingResults.value.add(resultId);

    try {
      // Find the result being accepted
      const result = selectionComposable.selectedSampleResults.value.find((r) => r.id === resultId);

      if (!result) {
        // Result not found - likely already accepted or removed
        showResultSnackbar("warning", "Result not found - it may have been already processed", testName);
        return;
      }

      // Check if this is the LAST pending result for this test
      const pendingForThisTest = selectionComposable.selectedSampleResults.value.filter(
        (r) => r.mappingKey === result.mappingKey,
      );

      const isLastPendingForTest = pendingForThisTest.length === 1;

      // Accept via API
      const response: ApiResponse = await pendingComposable.publishResult(resultId);
      const validated = validateResponse(response);

      // Handle null response (error in usePending)
      if (validated.type === "null") {
        if (pendingComposable.error.value) {
          const result = handleError(pendingComposable.error.value, {
            operation: "acceptResult",
            fallbackKey: "pages.validation.results.pending.acceptError",
          });
          showResultSnackbar("error", result.message, testName);
          pendingComposable.error.value = null;
        }
        return;
      }

      // Handle array response with specific error
      if (validated.type === "error" && validated.errors.length > 0) {
        const result = handleError(validated.errors[0]?.error, {
          operation: "acceptResult",
          fallbackKey: "pages.validation.results.pending.acceptError",
        });
        showResultSnackbar("error", result.message, testName);
        pendingComposable.error.value = null;
        return;
      }

      // Handle success (both array and boolean)
      if (validated.type === "success" || validated.type === "partial") {
        // Show success message
        const successMessage = t("pages.validation.results.pending.acceptSuccess");
        showResultSnackbar("success", successMessage, testName);

        // Extract accepted value from API response
        let acceptedValue = result.displayResult;

        if (Array.isArray(validated.data) && validated.data[0]) {
          // API returns array with accepted result data
          const apiResult = validated.data[0];
          acceptedValue = apiResult.result || apiResult.value || result.displayResult;
        }

        // Refresh visit ONLY if this is the last pending result for this test
        if (isLastPendingForTest && selectionComposable.selectedSample.value) {
          const barcode = selectionComposable.selectedSample.value.barcode;

          try {
            const visit = await lisComposable.fetchVisitBySample(barcode, true);

            if (visit) {
              // Update visit BEFORE removing from pending
              selectionComposable.selectedSampleVisit.value = visit;
            }
          } catch (error) {
            console.error("[acceptResult] Failed to refresh data:", error);
          }
        }

        // Call onSuccess with accepted value
        await onSuccess(resultId, acceptedValue);

        return;
      }
      // Handle unknown response format
      if (validated.type === "unknown") {
        const warningMessage = `Unknown response status: ${validated.data?.status || "undefined"}`;
        showResultSnackbar("warning", warningMessage, testName);
        pendingComposable.error.value = null;
        return;
      }
    } catch (error: any) {
      // Use centralized error boundary for consistent error handling
      handleError(error, {
        operation: "acceptResult",
        fallbackKey: "pages.validation.results.pending.acceptError",
        onError: (message) => {
          showResultSnackbar("error", message, testName);
          pendingComposable.error.value = null;
        },
      });
    } finally {
      acceptingResults.value.delete(resultId);
    }
  };

  //
  // ============================================================================
  // PUBLIC API - BULK ACTIONS
  // ============================================================================
  //

  /**
   * Accept all results for a sample in one operation
   *
   * Purpose:
   * - Bulk publish all pending results for rapid workflow
   * - Validate and accept entire sample at once
   * - Minimize API calls and user interactions
   *
   * Responsibilities:
   * - Set acceptingAll flag to disable bulk action UI
   * - Call publishAllResults API with sample barcode
   * - Validate API response array (success/partial/error)
   * - Refresh visit data to get all updated current values
   * - Invoke success callback for navigation (move to next sample)
   * - Show appropriate notifications based on result
   * - Handle partial failures (some accepted, some failed)
   *
   * @param sample - Sample to accept all results for
   * @param pendingComposable - Pending data composable instance
   * @param lisComposable - LIS data composable instance
   * @param selectionComposable - Validation selection composable instance
   * @param onSuccess - Callback invoked on successful acceptance (no parameters)
   */
  const acceptAll = async (
    sample: PendingSample,
    pendingComposable: ReturnType<typeof usePending>,
    lisComposable: ReturnType<typeof useLis>,
    selectionComposable: ReturnType<typeof useValidationSelection>,
    onSuccess: () => Promise<void>,
  ): Promise<void> => {
    acceptingAll.value = true;

    try {
      const response: ApiResponse = await pendingComposable.publishAllResults(sample.barcode);
      const validated = validateResponse(response);

      // Handle null response (error in usePending)
      if (validated.type === "null") {
        if (pendingComposable.error.value) {
          const result = handleError(pendingComposable.error.value, {
            operation: "acceptAll",
            fallbackKey: "pages.validation.results.pending.acceptAllError",
          });
          showResultSnackbar("error", result.message);
          pendingComposable.error.value = null;
        }
        return;
      }

      // Handle complete failure
      if (validated.type === "error") {
        const errorCount = validated.errors.length;
        const result = handleError(validated.errors[0]?.error, {
          operation: "acceptAll",
          fallbackKey: "pages.validation.results.pending.acceptAllError",
        });

        const message = errorCount === 1 ? result.message : `${errorCount} results failed: ${result.message}`;

        showResultSnackbar("error", message);
        pendingComposable.error.value = null;
        return;
      }

      // Handle both success and partial success
      if (validated.type === "success" || validated.type === "partial") {
        showResultSnackbar("success", t("pages.validation.results.pending.acceptAllSuccess"));

        // Refresh visit data to get updated current results
        if (selectionComposable.selectedSample.value) {
          await lisComposable.fetchVisitBySample(sample.barcode, true);
        }

        // Clear acceptingAll BEFORE calling onSuccess (which navigates)
        acceptingAll.value = false;

        await onSuccess();

        return;
      }

      // Handle unknown response format
      if (validated.type === "unknown") {
        showResultSnackbar("warning", "Unknown response format");
        pendingComposable.error.value = null;
        return;
      }
    } catch (error: any) {
      // Use centralized error boundary for bulk accept operation
      handleError(error, {
        operation: "acceptAll",
        fallbackKey: "pages.validation.results.pending.acceptAllError",
        onError: (message) => {
          showResultSnackbar("error", message);
          pendingComposable.error.value = null;
        },
      });
    } finally {
      if (acceptingAll.value) {
        acceptingAll.value = false;
      }
    }
  };

  /**
   * Reject a single result and remove from pending queue
   *
   * Purpose:
   * - Delete a result that is erroneous or needs re-testing
   * - Remove from pending queue without publishing to LIS
   * - Provide user feedback on rejection status
   *
   * Responsibilities:
   * - Track loading state to prevent double-submission
   * - Call deleteResult API to remove from backend
   * - Validate API response (boolean: true = success, false = error)
   * - Show appropriate notification (warning for success, error for failure)
   * - Invoke success callback for UI update (remove from list)
   * - Handle network and server errors gracefully
   *
   * @param resultId - ID of the result to reject
   * @param testName - Display name of the test
   * @param pendingComposable - Pending data composable instance
   * @param onSuccess - Callback invoked on successful rejection (receives resultId)
   */
  const rejectResult = async (
    resultId: number,
    testName: string,
    pendingComposable: ReturnType<typeof usePending>,
    onSuccess: (resultId: number) => void,
  ): Promise<void> => {
    rejectingResults.value.add(resultId);

    try {
      const success: boolean = await pendingComposable.deleteResult(resultId);
      const validated = validateResponse(success);

      // Handle error response
      if (validated.type === "error" || success === false) {
        if (pendingComposable.error.value) {
          const result = handleError(pendingComposable.error.value, {
            operation: "rejectResult",
            fallbackKey: "pages.validation.results.pending.rejectError",
          });
          showResultSnackbar("error", result.message, testName);
          pendingComposable.error.value = null;
        }
        return;
      }

      // Handle success
      if (validated.type === "success" || success === true) {
        const successMessage = t("pages.validation.results.pending.rejectSuccess");
        showResultSnackbar("warning", successMessage, testName);
        onSuccess(resultId);
        return;
      }
    } catch (error: any) {
      // Use centralized error boundary for reject operation
      handleError(error, {
        operation: "rejectResult",
        fallbackKey: "pages.validation.results.pending.rejectError",
        onError: (message) => {
          showResultSnackbar("error", message, testName);
          pendingComposable.error.value = null;
        },
      });
    } finally {
      rejectingResults.value.delete(resultId);
    }
  };

  /**
   * Reject all results for a sample in one operation
   *
   * Purpose:
   * - Bulk delete all pending results when sample needs complete re-testing
   * - Clear entire pending queue for a sample
   * - Provide rapid cleanup for erroneous batches
   *
   * Responsibilities:
   * - Set rejectingAll flag to disable bulk action UI
   * - Call deleteAllResults API with sample barcode
   * - Validate boolean response (true = success, false = error)
   * - Show appropriate notification (warning-styled for success)
   * - Invoke success callback for navigation/refresh
   * - Handle API errors gracefully
   *
   * @param sample - Sample to reject all results for
   * @param pendingComposable - Pending data composable instance
   * @param onSuccess - Callback invoked on successful rejection (no parameters)
   */
  const rejectAll = async (
    sample: PendingSample,
    pendingComposable: ReturnType<typeof usePending>,
    onSuccess: () => Promise<void>,
  ): Promise<void> => {
    rejectingAll.value = true;

    try {
      const success: boolean = await pendingComposable.deleteAllResults(sample.barcode);
      const validated = validateResponse(success);

      // Handle error response (false)
      if (validated.type === "error" || success === false) {
        if (pendingComposable.error.value) {
          const result = handleError(pendingComposable.error.value, {
            operation: "rejectAll",
            fallbackKey: "pages.validation.results.pending.rejectAllError",
          });
          showResultSnackbar("error", result.message);
          pendingComposable.error.value = null;
        }
        return;
      }

      // Handle success (true)
      if (validated.type === "success" || success === true) {
        showResultSnackbar(
          "warning", // warning color for reject
          t("pages.validation.results.pending.rejectAllSuccess"),
        );
        await onSuccess();
        return;
      }
    } catch (error: any) {
      // Use centralized error boundary for bulk reject operation
      handleError(error, {
        operation: "rejectAll",
        fallbackKey: "pages.validation.results.pending.rejectAllError",
        onError: (message) => {
          showResultSnackbar("error", message);
          pendingComposable.error.value = null;
        },
      });
    } finally {
      rejectingAll.value = false;
    }
  };

  //
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  //

  return {
    // State (readonly)
    acceptingResults: readonly(acceptingResults),
    rejectingResults: readonly(rejectingResults),
    acceptingAll: readonly(acceptingAll),
    rejectingAll: readonly(rejectingAll),

    // Methods
    acceptResult,
    acceptAll,
    rejectResult,
    rejectAll,
  };
};
